import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { ITileID } from "../../models/product-compare";
import { unique } from "../../utils/functional";
import { defaults } from "./defaults";
import { loadSelectedTileID, loadTiles } from "./thunks";

/**
 * Redux slice for product comparison functionality.
 *
 * Manages state for:
 * - Comparison tiles (product data for each tile in the comparison grid)
 * - Selected tile IDs (tiles user has selected to compare)
 * - Tray UI state (expanded/collapsed, visibility)
 * - Price selection (e.g., selected size like "Queen")
 * - Root products (for configurator integration)
 */
const productCompareSlice = createSlice({
    name: "productcompare",
    initialState: defaults,
    reducers: {
        /**
         * Updates the size/price selection value (e.g., "Queen", "King").
         * This affects which product variants are displayed in the comparison.
         */
        updatePriceSelectValue: (
            state,
            action: PayloadAction<{
                value: string;
            }>,
        ) => {
            state.priceSelectValue = action.payload.value;
        },

        /**
         * Adds a tile to the user's selection for comparison.
         * Ensures no duplicates using unique filter.
         */
        addToSelected: (
            state,
            action: PayloadAction<{
                tileID: ITileID;
            }>,
        ) => {
            state.selectedTileIDs = [
                ...state.selectedTileIDs,
                action.payload.tileID,
            ].filter(unique);
        },

        /**
         * Removes a tile from the user's comparison selection.
         */
        removeFromSelected: (
            state,
            action: PayloadAction<{
                tileID: ITileID;
            }>,
        ) => {
            state.selectedTileIDs = state.selectedTileIDs.filter(
                (tileID) => tileID !== action.payload.tileID,
            );
        },

        /**
         * Collapses the selected products tray UI.
         */
        collapseTray: (state) => {
            state.trayIsExpanded = false;
            state.trayLabel = "expand";
        },

        /**
         * Expands the selected products tray UI.
         */
        expandTray: (state) => {
            state.trayIsExpanded = true;
            state.trayLabel = "collapse";
        },

        /**
         * Shows the comparison modal.
         */
        showModal: (state) => {
            state.showCompareModal = true;
        },

        /**
         * Hides the comparison modal.
         */
        hideModal: (state) => {
            state.showCompareModal = false;
        },
    },
    extraReducers: (builder) => {
        // Handle successful tile loading
        builder.addCase(loadTiles.fulfilled, (state, action) => {
            state.tiles = action.payload as typeof state.tiles;
        });
        // Handle adding a selected tile ID from URL params
        builder.addCase(loadSelectedTileID.fulfilled, (state, action) => {
            state.selectedTileIDs = [
                ...state.selectedTileIDs,
                action.payload,
            ].filter(unique);
        });
    },
});

export const {
    updatePriceSelectValue,
    addToSelected,
    removeFromSelected,
    collapseTray,
    expandTray,
    showModal,
    hideModal,
} = productCompareSlice.actions;

export const reducers = productCompareSlice.reducer;
